import { C } from '../../theme/participantTokens';

interface CascadeItem {
  rank: number;
  title: string;
  score: number;
}

interface PriorityCascadeProps {
  items: CascadeItem[];
  maxItems?: number;
}

export function PriorityCascade({ items, maxItems = 6 }: PriorityCascadeProps) {
  const slice = items.slice(0, maxItems);
  if (slice.length === 0) return null;

  const maxScore = Math.max(...slice.map(i => i.score), 1);
  const chartW = 700;
  const rowH = 36;   // was 26 — increased to prevent label/bar overlap across rows

  return (
    <div
      className="pp-results-enter"
      style={{
        background: C.surface,
        border: `1px solid ${C.rule}`,
        borderRadius: 4,
        padding: '16px 18px',
        marginBottom: 18,
      }}
    >
      <div
        style={{
          fontFamily: 'JetBrains Mono',
          fontSize: '0.78rem',
          fontWeight: 600,
          letterSpacing: '0.16em',
          color: C.textTer,
          textTransform: 'uppercase',
          marginBottom: 14,
          paddingBottom: 8,
          borderBottom: `1px solid ${C.rule}`,
        }}
      >
        Priority Cascade — Final Rankings
      </div>
      <svg
        width="100%"
        height={slice.length * rowH + 20}
        viewBox={`0 0 ${chartW + 60} ${slice.length * rowH + 20}`}
        preserveAspectRatio="xMinYMid meet"
      >
        <defs>
          <filter id="pp-cascade-glow">
            <feGaussianBlur stdDeviation="1.5" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <linearGradient id="pp-cascade-grad" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0%" stopColor={C.mustard} stopOpacity={0.75} />
            <stop offset="100%" stopColor={C.mustard} stopOpacity={0.2} />
          </linearGradient>
        </defs>
        {slice.map((item, idx) => {
          const y = idx * rowH + 14;  // was +8; extra top clearance so text cap isn't clipped
          const barW = (item.score / maxScore) * chartW;
          const opacity = Math.max(0.35, 0.85 - idx * 0.08);
          const delay = 0.1 + idx * 0.08;
          return (
            <g key={item.rank}>
              <text x={0} y={y - 2} fontFamily="JetBrains Mono" fontSize={11} fill={C.textDim} letterSpacing={0.5}>
                {String(item.rank).padStart(2, '0')} {item.title.toUpperCase().slice(0, 42)}
              </text>
              <rect
                x={0}
                y={y + 6}
                width={barW}
                height={14}
                rx={1}
                fill="url(#pp-cascade-grad)"
                className="pp-draw-bar"
                opacity={opacity}
                filter={idx === 0 ? 'url(#pp-cascade-glow)' : undefined}
                style={{ animationDelay: `${delay}s`, transformOrigin: 'left center' }}
              />
              <text
                x={barW + 6}
                y={y + 14}
                fontFamily="JetBrains Mono"
                fontSize={13}
                fontWeight={700}
                fill={C.mustard}
                opacity={opacity}
              >
                {item.score.toFixed(1)}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
